import { useState } from "react";
import type {
  InputHTMLAttributes,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "../../lib/format";
import { useT } from "../../i18n";

const fieldInput =
  "h-11 w-full rounded border bg-surface px-3.5 text-sm text-ink outline-none transition-colors placeholder:text-faint focus:border-brand";

interface FieldShellProps {
  label: string;
  error?: string;
  /** Span both columns of a 2-column form grid. */
  full?: boolean;
  /** Small grey line under the field (hidden while there's an error). */
  hint?: ReactNode;
  children: ReactNode;
}

function FieldShell({ label, error, full, hint, children }: FieldShellProps) {
  return (
    <label className={cn("flex flex-col gap-1.5", full && "sm:col-span-2")}>
      <span className="text-[13px] font-medium text-ink-soft">{label}</span>
      {children}
      {error ? <span className="text-xs text-tone-red-fg">{error}</span> : hint && <span className="text-[10px] text-muted">{hint}</span>}
    </label>
  );
}

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  full?: boolean;
  /** Small icon shown inside the input, on the left. */
  icon?: ReactNode;
  hint?: ReactNode;
  ref?: Ref<HTMLInputElement>;
}

/** Label above a grey input; spread react-hook-form's `register()` onto it. */
export function TextField({ label, error, full, icon, hint, ...input }: TextFieldProps) {
  const field = (
    <input
      aria-invalid={!!error}
      className={cn(
        fieldInput,
        error ? "border-tone-red-fg" : "border-transparent",
        !!icon && "pl-9",
      )}
      {...input}
    />
  );
  return (
    <FieldShell label={label} error={error} full={full} hint={hint}>
      {icon ? (
        <span className="relative flex">
          <span className="pointer-events-none absolute top-1/2 left-3.5 flex -translate-y-1/2 text-muted">{icon}</span>
          {field}
        </span>
      ) : (
        field
      )}
    </FieldShell>
  );
}

export function PasswordField({ label, error, full, ...input }: TextFieldProps) {
  const t = useT();
  const [visible, setVisible] = useState(false);

  return (
    <FieldShell label={label} error={error} full={full}>
      <span className="relative">
        <input
          aria-invalid={!!error}
          className={cn(
            fieldInput,
            "pr-11",
            error ? "border-tone-red-fg" : "border-transparent",
          )}
          {...input}
          type={visible ? "text" : "password"}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? t.common.hidePassword : t.common.showPassword}
          className="absolute inset-y-0 right-0 flex w-11 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-muted hover:text-ink"
        >
          {visible ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </span>
    </FieldShell>
  );
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: readonly string[];
  /** Display text per option value; values without an entry are shown as-is. */
  labels?: Record<string, string>;
  error?: string;
  full?: boolean;
  ref?: Ref<HTMLSelectElement>;
}

export function SelectField({ label, options, labels, error, full, ...select }: SelectFieldProps) {
  return (
    <FieldShell label={label} error={error} full={full}>
      <select
        aria-invalid={!!error}
        className={cn(
          fieldInput,
          "px-2.5",
          error ? "border-tone-red-fg" : "border-transparent",
        )}
        {...select}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {labels?.[o] ?? o}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string;
  full?: boolean;
  ref?: Ref<HTMLTextAreaElement>;
}

export function TextAreaField({ label, error, full, ...area }: TextAreaFieldProps) {
  return (
    <FieldShell label={label} error={error} full={full}>
      <textarea
        rows={3}
        aria-invalid={!!error}
        className={cn(
          "w-full resize-y rounded border bg-surface px-3.5 py-3 text-sm text-ink outline-none transition-colors placeholder:text-faint focus:border-brand",
          error ? "border-tone-red-fg" : "border-transparent",
        )}
        {...area}
      />
    </FieldShell>
  );
}

const ALERT_TONES = {
  error: "bg-tone-red-bg text-[#7A1A10]",
  success: "bg-tone-green-bg text-tone-green-fg",
  warning: "bg-tone-amber-bg text-[#5A3A00]",
  info: "bg-tone-blue-bg text-[#1F3A86]",
};

interface FormAlertProps {
  tone?: keyof typeof ALERT_TONES;
  title?: string;
  children: ReactNode;
}

/** Coloured banner, matching the company area's status banners. */
export function FormAlert({ tone = "error", title, children }: FormAlertProps) {
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={cn(
        "rounded-card px-4 py-3.5 text-sm leading-normal",
        ALERT_TONES[tone],
      )}
    >
      {title && <b className="font-medium">{title}</b>} {children}
    </div>
  );
}
